/**
 * My Accounts → Actions → History, V1: what each record and event says.
 *
 * States are the spec's four (signal-service-design-note §7): Open (active),
 * Handled (acknowledged — someone marked it handled), Snoozed (this viewer
 * snoozed it; Snooze is per user, so it comes from `my_snoozed_until`, not
 * the shared state) and Resolved. "Generated", "viewed" and "delivered" are
 * never shown: the first is shown as "Appeared", the other two are never
 * recorded.
 */
import { SIGNAL_COPY } from "./signalCTAs";
import type { SignalTier } from "../../Store/Reducers/SignalFeedSlice";

export interface HistoryEvent {
  event: string;
  at: string | null;
  actor: string | null;
  is_me?: boolean;
  persona?: string | null;
  cta_label?: string | null;
  guide_step?: string | null;
  done?: boolean | null;
  snooze_days?: number | null;
  snoozed_until?: string | null;
  reason?: string | null;
}

export interface HistoryRecord {
  firing_id: string;
  account_id: string;
  account_name: string;
  signal_key: string;
  title: string;
  tier: SignalTier;
  state: string;
  resolution_reason: string | null;
  my_snoozed_until?: string | null;
  last_activity_at?: string | null;
  events: HistoryEvent[];
}

export type HistoryState = "active" | "acknowledged" | "snoozed" | "resolved";

export const STATE_LABEL: Record<HistoryState, string> = {
  active: "Open",
  acknowledged: "Handled",
  snoozed: "Snoozed",
  resolved: "Resolved",
};

export const STATE_ORDER: HistoryState[] = [
  "active",
  "acknowledged",
  "snoozed",
  "resolved",
];

const PERSONA_LABEL: Record<string, string> = {
  ae: "AE",
  csm: "CSM",
  sponsor: "Sponsor",
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Oct 28". */
export function shortDate(value: string | null | undefined): string {
  const d = toDate(value);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}` : "";
}

/** "Oct 28, 10:42 AM". */
export function dateTime(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${shortDate(value)}, ${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
}

/** The state this viewer sees: their own snooze wins over the shared state. */
export function displayState(r: HistoryRecord): HistoryState {
  if (r.my_snoozed_until && r.state !== "resolved") return "snoozed";
  if (r.state === "acknowledged" || r.state === "resolved") return r.state;
  return "active";
}

function firstName(name: string | null): string {
  return (name ?? "").trim().split(/\s+/)[0] || "a teammate";
}

/** The short line under the state badge. */
export function stateReason(r: HistoryRecord): string {
  const state = displayState(r);
  if (state === "snoozed") return `until ${shortDate(r.my_snoozed_until)}`;
  if (state === "acknowledged") {
    const handled = r.events.filter(e => e.event === "dismissed").pop();
    if (!handled) return "";
    return handled.is_me ? "by you" : `by ${firstName(handled.actor)}`;
  }
  if (state === "resolved") {
    const reason = r.resolution_reason ?? "";
    if (reason === "condition_cleared:resolved_after_action")
      return "after action";
    if (reason.startsWith("covered_by:")) {
      const key = reason.slice("covered_by:".length);
      return `covered by ${SIGNAL_COPY[key]?.title ?? "another signal"}`;
    }
    return "data changed";
  }
  return "";
}

export interface EventLine {
  /** "You" or the teammate's name; null for system steps. */
  who: string | null;
  role: string | null;
  verb: string;
  /** Shown in bold after the verb (a button or step name). */
  object: string | null;
  at: string | null;
}

/** One step of the trail in plain words, or null for steps users never see. */
export function eventLine(e: HistoryEvent): EventLine | null {
  const who = e.actor || e.is_me ? (e.is_me ? "You" : e.actor) : null;
  const role = e.persona ? (PERSONA_LABEL[e.persona] ?? null) : null;
  const line = (verb: string, object: string | null = null): EventLine => ({
    who,
    role,
    verb,
    object,
    at: e.at,
  });
  switch (e.event) {
    case "generated":
      return {
        who: null,
        role: null,
        verb: "Appeared",
        object: null,
        at: e.at,
      };
    case "cta_clicked":
      return e.cta_label
        ? line("clicked", e.cta_label)
        : line("clicked the action");
    case "guide_step":
      return e.guide_step
        ? line(e.done ? "ticked" : "unticked", e.guide_step)
        : line(e.done ? "ticked a step" : "unticked a step");
    case "snoozed":
      return line(
        e.snooze_days
          ? `snoozed it for ${e.snooze_days} day${e.snooze_days === 1 ? "" : "s"}`
          : "snoozed it"
      );
    case "dismissed":
      return line("marked it handled");
    case "resolved":
      return {
        who: null,
        role: null,
        verb:
          e.reason === "condition_cleared:resolved_after_action"
            ? "Resolved after action"
            : "Resolved",
        object: null,
        at: e.at,
      };
    default:
      return null;
  }
}

/** When the record last changed, for sorting and the right-hand date. */
export function lastActivity(r: HistoryRecord): string | null {
  if (r.last_activity_at) return r.last_activity_at;
  const times = r.events.map(e => e.at).filter((t): t is string => !!t);
  return times.sort().pop() ?? null;
}
